// src/agents/CustomerProfilePage.jsx 
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { showError, showSuccess } from "../utils/toast"; // adjust path
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const CustomerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");
  const [customer, setCustomer] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    address: "",
    dob: "",
    phone: "",
  });

  // NEW: reminder modal + message state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderMsg, setReminderMsg] = useState("");

  // NEW: Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/customer/${id}`);
        if (!res.ok) throw new Error("Failed to fetch customer data");
        const data = await res.json();

        if (!data.agentId || !data.customerPhone || !data.customerEmail) {
          console.error("Missing agentId / phone / email from backend:", data);
          return;
        }

        const formatDate = (d) =>
          d ? new Date(d).toLocaleDateString("en-GB") : "-";

        const formattedCustomer = {
          name: data.customerName,
          email: data.customerEmail,
          contact: data.customerPhone,
          address: data.customerAddress || "-",
          dob: formatDate(data.customerDOB) || "-",
          agentId: data.agentId,
          policies: [data],
        };

        const policyRes = await fetch(
          `${import.meta.env.VITE_API_URL}/api/customer-policies/${data.agentId}/${data.customerPhone}/${data.customerEmail}`
        );
        if (!policyRes.ok) throw new Error("Failed to fetch policy data");
        const policies = await policyRes.json();

        formattedCustomer.policies = Array.isArray(policies) ? policies : [];
        setCustomer(formattedCustomer);
      } catch (err) {
        console.error("❌ Error fetching customer data:", err);
      }
    };

    if (id) fetchCustomer();
  }, [id]);

  if (!customer) return <div className="container py-4">Loading...</div>;

  const totalPremium = customer.policies.reduce(
    (sum, p) => sum + Number(p?.policyDetails?.premium || 0),
    0
  );

  const nextDue = customer.policies.reduce((latest, p) => {
    const due = new Date(p?.policyDetails?.endDate);
    return !latest || due < new Date(latest) ? p?.policyDetails?.endDate : latest;
  }, null);

  // nice formatted due for the default message
  const nextDueStr = nextDue ? new Date(nextDue).toLocaleDateString("en-GB") : "—";

  const handleEditChange = (e) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {};
      if (formData.email?.trim())
        payload.customerEmail = formData.email.trim();
      if (formData.phone?.trim())
        payload.customerPhone = formData.phone.trim();
      if (formData.address?.trim())
        payload.customerAddress = formData.address.trim();
      if (formData.dob?.trim()) {
        const d = new Date(formData.dob);
        if (!isNaN(d)) payload.customerDOB = d.toISOString();
      }
      if (Object.keys(payload).length === 0) {
        setShowEditModal(false);
        return;
      }

      const results = await Promise.all(
        (customer.policies || []).map((p) =>
          fetch(`${import.meta.env.VITE_API_URL}/api/policies/update/${p._id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }).then((r) => r.json())
        )
      );

      const updatedPolicies = results.map((r) => r.updatedPolicy || r);

      setCustomer((prev) => ({
        ...prev,
        email: payload.customerEmail ?? prev.email,
        contact: payload.customerPhone ?? prev.contact,
        address: payload.customerAddress ?? prev.address,
        dob: payload.customerDOB
          ? new Date(payload.customerDOB).toLocaleDateString("en-GB")
          : prev.dob,
        policies: prev.policies.map(
          (old) => updatedPolicies.find((u) => u._id === old._id) || old
        ),
      }));

      setShowEditModal(false);
    } catch (err) {
      console.error("❌ Error updating customer:", err);
    }
  };

  const handleSendMessage = async (person, message) => {
    try {
      console.log("Sending message to:", person, "with message:", message);
      const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/notification/send-notification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message,
          phone: person.customerPhone,
          agentId: person.agentId,
          policyId: person._id
        }),
      });

      // guard if helper isn't in scope
      if (typeof fetchNotification === "function") {
        fetchNotification();
      }
      console.log("Response from send-notification:", res);
      if (!res.ok) throw new Error("Failed to send notification");
      alert("✅ Message sent successfully!");
    } catch (error) {
      alert("❌ Failed to send notifications");
      console.log(error);
    }
  };

  // NEW: create a sensible default message when opening the reminder modal
  const buildDefaultReminder = () => {
    const name = customer?.name ? `Hi ${customer.name},` : "Hello,";
    const duePart =
      nextDueStr !== "—"
        ? ` This is a friendly reminder that your premium is due on ${nextDueStr}.`
        : " This is a friendly reminder about your upcoming premium.";
    return `${name}${duePart}`;
  };

  // NEW: send from modal
  const onConfirmSendReminder = async () => {
    if (!customer?.contact) {
      showError?.("Customer phone not available");
      return;
    }
    console.log(" customer value Reminder message to send:", customer);
    const person = {
      customerPhone: customer.contact,
      agentId: customer.agentId,
      _id: customer.policies[0]._id
    };
    const msg = (reminderMsg || "").trim();
    if (!msg) {
      showError?.("Message cannot be empty");
      return;
    }
    await handleSendMessage(person, msg);
    setShowReminderModal(false);
    // showSuccess?.("Reminder queued to send");
  };

  // NEW: Download PDF function
  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();

      // Logo configuration - Add logo to top right corner
      const logoUrl = '../../public/Logo_pdf.webp';
      const logoWidth = 30; // Logo width in mm
      const logoHeight = 25; // Logo height in mm
      const pageWidth = doc.internal.pageSize.getWidth();
      const logoX = pageWidth - logoWidth - 14; // Position from right edge with 14mm margin
      const logoY = 10; // Position from top

      // Add logo to first page (top right corner)
      try {
        doc.addImage(logoUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
      } catch (logoError) {
        console.warn("Logo not loaded:", logoError);
      }

      // Title
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Customer Profile Report", 14, 20);

      // Date
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated on: ${new Date().toLocaleDateString("en-GB")}`, 14, 28);

      // Personal Information
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Personal Information", 14, 40);

      const personalInfo = [
        ["Name", customer.name || "-"],
        ["Customer ID", (parseInt(id) + 1).toString()],
        ["Email", customer.email || "-"],
        ["Contact", customer.contact || "-"],
        ["Address", customer.address || "-"],
        ["Date of Birth", customer.dob || "-"],
      ];

      autoTable(doc, {
        startY: 45,
        head: [["Field", "Value"]],
        body: personalInfo,
        theme: "striped",
        headStyles: { fillColor: [41, 128, 185] },
        margin: { left: 14 },
      });

      // Policy Summary
      let finalY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Policy Summary", 14, finalY);

      finalY += 5;
      const policySummary = [
        ["Total Policies", customer.policies.length.toString()],
        ["Total Premium", `${totalPremium.toLocaleString()}`],
        ["Next Due Date", nextDueStr],
        ["Upcoming (7 days)", customer.policies.filter((p) => {
          const due = new Date(p?.policyDetails?.endDate);
          const now = new Date();
          const diff = (due - now) / (1000 * 3600 * 24);
          return diff >= 0 && diff <= 7;
        }).length.toString()],
      ];

      autoTable(doc, {
        startY: finalY,
        head: [["Metric", "Value"]],
        body: policySummary,
        theme: "striped",
        headStyles: { fillColor: [41, 128, 185] },
        margin: { left: 14 },
      });

      // Policy Details
      if (customer.policies.length > 0) {
        finalY = doc.lastAutoTable.finalY + 15;

        if (finalY > 250) {
          doc.addPage();
          finalY = 20;
          
          // Add logo to new page (top right corner)
          try {
            doc.addImage(logoUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
          } catch (logoError) {
            console.warn("Logo not loaded on new page:", logoError);
          }
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Policy Details", 14, finalY);

        finalY += 5;
        const policyDetails = customer.policies.map((policy, index) => {
          const title = policy?.policyDetails?.policyName || 'Health Insurance';
          const code = policy?.policyDetails?.policyNumber || "123XYZ";
          const premium = policy?.policyDetails?.premium || 0;
          const startDate = policy?.policyDetails?.startDate
            ? new Date(policy.policyDetails.startDate).toLocaleDateString("en-GB")
            : "-";
          const endDate = policy?.policyDetails?.endDate
            ? new Date(policy.policyDetails.endDate).toLocaleDateString("en-GB")
            : "-";

          return [
            (index + 1).toString(),
            title,
            code,
            `${Number(premium).toLocaleString()}`,
            startDate,
            endDate,
            "Active"
          ];
        });

        autoTable(doc, {
          startY: finalY,
          head: [["#", "Policy Name", "Policy Number", "Premium", "Start Date", "End Date", "Status"]],
          body: policyDetails,
          theme: "striped",
          headStyles: { fillColor: [41, 128, 185] },
          margin: { left: 14 },
          styles: { fontSize: 8 },
          // Add logo to each new page created by autoTable
          didDrawPage: (data) => {
            if (data.pageNumber > 1) {
              try {
                doc.addImage(logoUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
              } catch (logoError) {
                console.warn("Logo not loaded on autoTable page:", logoError);
              }
            }
          },
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" }
        );
      }

      // Save PDF - Fixed: Added proper null check for customer name
      const customerName = customer?.name ? customer.name.replace(/\s+/g, "_") : "Customer";
      const timestamp = new Date().getTime();
      doc.save(`Customer_${customerName}_${timestamp}.pdf`);
      
      setShowExportModal(false);
      showSuccess?.("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showError?.("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <div className="customer-profile container py-4">
      {/* Header */}
      <div className="d-flex justify-content-between flex-wrap align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-light border d-flex align-items-center gap-2"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left"></i> Back to Customers
          </button>
          <div>
            <h3 className="fw-bold mb-0">
              {customer.name}
            </h3>
            <small className="text-muted d-block">
              Customer ID: {parseInt(id) + 1}
            </small>
          </div>
        </div>

        {/* export user data */}
        {/* Export & Actions Section */}
<div className="cp-cta d-flex gap-3 mt-3 mt-md-0">
  {/* Export Data Button */}
  <button
    type="button"
    className="pill-btn teal"
    title="Export customer data"
    onClick={() => {
      setShowExportModal(true);
    }}
  >
    <i className="bi bi-download" />
    <span>Download Pdf</span>
  </button>

  {/* Send Reminder Button */}
  <button
    type="button"
    className="pill-btn teal"
    title="Send reminder to the customer"
    onClick={() => {
      setReminderMsg(buildDefaultReminder());
      setShowReminderModal(true);
    }}
  >
    <i className="bi bi-bell" />
    <span>Send Reminder</span>
  </button>
</div>
      </div>

      {/* Tabs (kept single tab for now) */}
      {activeTab === "overview" && (
        <div className="row">
          {/* LEFT */}
          <div className="col-md-8">
            {/* Personal Info – compact & tidy like the mock */}
            <div className="bg-white rounded shadow-sm p-4 mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">Personal Information</h5>

                <button
                  type="button"
                  className="edit-chip"
                  onClick={() => {
                    setFormData({
                      email: customer.email || '',
                      phone: customer.contact || '',
                      address: customer.address || '',
                      dob: ''
                    });
                    setShowEditModal(true);
                  }}
                >
                  <i className="bi bi-pencil-square" />
                  <span>Edit</span>
                </button>
              </div>

              {/* Four-column grid: label/value + label/value */}
              <div className="info-grid">
                <div className="rowline">
                  <div className="k">Email:</div>
                  <div className="v">{customer.email || '-'}</div>
                  <div className="k">Occupation:</div>
                  <div className="v">{customer.occupation || '-'}</div>
                </div>

                <div className="rowline">
                  <div className="k">Phone:</div>
                  <div className="v">{customer.contact || '-'}</div>
                  <div className="k">Address:</div>
                  <div className="v">{customer.address || '-'}</div>
                </div>

                <div className="rowline">
                  <div className="k">Date of Birth:</div>
                  <div className="v">{customer.dob || '-'}</div>
                  <div className="k d-none d-md-block" />
                  <div className="v d-none d-md-block" />
                </div>
              </div>
            </div>

            {/* Insurance Summary */}
            <div className="bg-white rounded shadow-sm p-4 ins-summary-card">
              <h5 className="fw-bold mb-3">Insurance Summary</h5>

              {customer.policies.map((p, i) => {
                const renewalText = (() => {
                  const d = p?.policyDetails?.endDate ? new Date(p.policyDetails.endDate) : null;
                  return d && !isNaN(d.getTime())
                    ? d.toLocaleString("en-GB", { month: "long", year: "numeric" })
                    : "—";
                })();

                const premiumINR = `₹${Number(p?.policyDetails?.premium || 0).toLocaleString("en-IN")}`;

                const title =
                  p?.policyType || p?.type || "Policy";

                const code =
                  p?.policyNumber || p?.policyId || p?.policyCode || "";

                return (
                  <div key={i} className="ins-item">
                    {/* left: title + code + renewal */}
                    <div className="ins-left">
                      <div className="ins-title">{title}</div>
                      {code && <div className="ins-code">{code}</div>}
                      <div className="ins-renewal">Renewal: {renewalText}</div>
                    </div>

                    {/* right: status + amount */}
                    <div className="ins-right">
                      <span className="status-chip status-active">active</span>
                      <div className="ins-amount">{premiumINR}</div>
                    </div>
                  </div>
                );
              })}

              {customer.policies.length === 0 && (
                <div className="text-muted small">No policies found.</div>
              )}
            </div>
          </div>

          {/* RIGHT */}
          <div className="col-md-4">
            {/* Quick Stats */}
            <div className="bg-white rounded shadow-sm p-4 mb-4">
              <h5 className="fw-bold mb-3">Quick Stats</h5>

              <div className="qs-row">
                <div className="qs-label">Total Policies</div>
                <div className="qs-value">{customer.policies.length}</div>
              </div>
              <div className="qs-row">
                <div className="qs-label">Total Premium</div>
                <div className="qs-value">₹{totalPremium.toLocaleString()}</div>
              </div>
              <div className="qs-row">
                <div className="qs-label">Next Due</div>
                <div className="qs-value">
                  {nextDue
                    ? new Date(nextDue).toLocaleDateString("en-GB")
                    : "—"}
                </div>
              </div>
              <div className="qs-row">
                <div className="qs-label">Upcoming (7 days)</div>
                <div className="qs-value text-danger">
                  {
                    customer.policies.filter((p) => {
                      const due = new Date(p?.policyDetails?.endDate);
                      const now = new Date();
                      const diff = (due - now) / (1000 * 3600 * 24);
                      return diff >= 0 && diff <= 7;
                    }).length
                  }
                </div>
              </div>
            </div>

            {/* Notes & Help – with icons & usage tips */}
            <div className="bg-white rounded shadow-sm p-4 cp-notes">
              <h5 className="fw-bold mb-3">How to use actions</h5>

              <hr className="my-3" />

              <ul className="list-unstyled mb-3 cp-actions">
                <li className="tip">
                  <i className="bi bi-pencil-square" />
                  <div>
                    <div className="tip-title">Edit</div>
                    <div className="tip-text">
                      Update phone, email, address or DOB. Changes are synced to
                      all policies for this customer.
                    </div>
                  </div>
                </li>
                <li className="tip">
                  <i className="bi bi-envelope" />
                  <div>
                    <div className="tip-title">Send Reminder</div>
                    <div className="tip-text">
                      Use the <em>Send Reminder</em> button (top-right) to
                      notify customers about upcoming premium due dates.
                    </div>
                  </div>
                </li>
                <li className="tip">
                  <i className="bi bi-download" />
                  <div>
                    <div className="tip-title">Export Data</div>
                    <div className="tip-text">
                      Download customer details and policy summary as a file for
                      reports or sharing.
                    </div>
                  </div>
                </li>
              </ul>

              <div className="help-box">
                <i className="bi bi-life-preserver" />
                <div>
                  <div className="fw-semibold">Need help?</div>
                  <div className="text-muted small">
                    Email{" "}
                    <a href="mailto:support@insurepro.example">
                      support@insurepro.example
                    </a>{" "}
                    or call <a href="tel:+1800123456">+1 800 123 456</a>.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Personal Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleEditChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  const onlyNumsAndPlus = e.target.value.replace(/[^0-9+]/g, "");
                  handleEditChange({
                    target: { name: "phone", value: onlyNumsAndPlus },
                  });
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleEditChange}
              />
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type="date"
                name="dob"
                value={formData.dob || ""}
                onChange={handleEditChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* NEW: Send Reminder Modal */}
      <Modal show={showReminderModal} onHide={() => setShowReminderModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send Custom Reminder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={reminderMsg}
                onChange={(e) => setReminderMsg(e.target.value)}
                placeholder="Type your custom reminder message..."
              />
              <Form.Text className="text-muted">
                Tip: mention due date {nextDueStr !== "—" ? `(${nextDueStr})` : ""} and policy number if needed.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setReminderMsg(buildDefaultReminder())}>
            Use Default
          </Button>
          <Button variant="secondary" onClick={() => setShowReminderModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirmSendReminder}>
            Send
          </Button>
        </Modal.Footer>
      </Modal>

      {/* NEW: Export Data Modal */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Export Customer Data</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <h6 className="mb-3 fw-bold">Personal Information</h6>
          <Table striped bordered hover responsive className="mb-4">
            <tbody>
              <tr>
                <td className="fw-semibold" style={{ width: "40%" }}>Name</td>
                <td>{customer.name || "-"}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Customer ID</td>
                <td>{parseInt(id) + 1}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Email</td>
                <td>{customer.email || "-"}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Contact</td>
                <td>{customer.contact || "-"}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Address</td>
                <td>{customer.address || "-"}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Date of Birth</td>
                <td>{customer.dob || "-"}</td>
              </tr>
            </tbody>
          </Table>

          <h6 className="mb-3 fw-bold">Policy Summary</h6>
          <Table striped bordered hover responsive className="mb-4">
            <tbody>
              <tr>
                <td className="fw-semibold" style={{ width: "40%" }}>Total Policies</td>
                <td>{customer.policies.length}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Total Premium</td>
                <td>₹{totalPremium.toLocaleString()}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Next Due Date</td>
                <td>{nextDueStr}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Upcoming (7 days)</td>
                <td className="text-danger fw-bold">
                  {
                    customer.policies.filter((p) => {
                      const due = new Date(p?.policyDetails?.endDate);
                      const now = new Date();
                      const diff = (due - now) / (1000 * 3600 * 24);
                      return diff >= 0 && diff <= 7;
                    }).length
                  }
                </td>
              </tr>
            </tbody>
          </Table>

          <h6 className="mb-3 fw-bold">Policy Details</h6>
          {customer.policies.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Policy Name</th>
                  <th>Policy Number</th>
                  <th>Premium</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {customer.policies.map((policy, index) => {
                  const title = policy?.policyDetails?.policyName || "Untitled Policy";
                  const code = policy?.policyDetails?.policyNumber || "-";
                  const premium = policy?.policyDetails?.premium || 0;
                  const startDate = policy?.policyDetails?.startDate
                    ? new Date(policy.policyDetails.startDate).toLocaleDateString("en-GB")
                    : "-";
                  const endDate = policy?.policyDetails?.endDate
                    ? new Date(policy.policyDetails.endDate).toLocaleDateString("en-GB")
                    : "-";

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{title}</td>
                      <td>{code}</td>
                      <td>₹{Number(premium).toLocaleString()}</td>
                      <td>{startDate}</td>
                      <td>{endDate}</td>
                      <td><span className="badge bg-success">Active</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No policies found.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleDownloadPDF}>
            <i className="bi bi-download me-2" />
            Download PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerProfilePage;